/**
 * FinopsPage — Dashboard FinOps (S5.4-13)
 *
 * KPIs COMEX (source: GET /api/v1/finops/data) :
 *   - Coût moyen / dossier   → target < 0.20€
 *   - Durée moyenne          → target < 2 min
 *   - Taux de succès         → target > 95%
 *   - Confiance moyenne      → target > 85%
 *
 * Visualisation : LineChart coût journalier (recharts).
 * Contrainte : zéro boucle for/while.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { useSDK } from '../context/SDKContext.tsx';
import type { FinopsKpiData, FinopsDashboard } from '@assistia/sdk';

// ── Seuils COMEX ─────────────────────────────────────────────────────────────
const TARGET_COST_EUR = 0.20;
const TARGET_DURATION_MS = 120_000;
const TARGET_SUCCESS_PCT = 95;
const TARGET_CONFIDENCE_PCT = 85;

// ── Composants ────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  target: string;
  ok: boolean;
  unit?: string;
}

function KpiCard({ label, value, target, ok }: KpiCardProps): React.ReactElement {
  return (
    <div className={`rounded-lg border p-5 shadow-sm ${ok ? 'border-border bg-card' : 'border-orange-500/30 bg-orange-500/5'}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${ok ? 'text-foreground' : 'text-orange-600'}`}>{value}</p>
      <div className={`mt-1 flex items-center gap-1 text-xs ${ok ? 'text-green-600' : 'text-orange-500'}`}>
        {ok ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
        <span>Cible : {target}</span>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FinopsPage(): React.ReactElement {
  const { sdk, isSimulated } = useSDK();
  const [kpi, setKpi] = useState<FinopsKpiData | null>(null);
  const [dashboard, setDashboard] = useState<FinopsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Retrieve KPI data
      const kpiRes = await sdk.finops.getFinopsKpi();
      setKpi(kpiRes.data);
      // Retrieve dashboard data if method exists (tests may not mock it)
      if (typeof sdk.finops.getDashboard === 'function') {
        const dashboardRes = await sdk.finops.getDashboard();
        setDashboard(dashboardRes.data);
      } else {
        setDashboard(null);
      }
      setLastRefresh(new Date());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  useEffect(() => { void load(); }, [load]);

  const durationMin = kpi ? (kpi.avg_duration_ms / 60_000).toFixed(1) : '—';
  const isFirstLoad = !kpi && !dashboard;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">FinOps</h1>
          <p className="text-sm text-muted-foreground mt-1">
            KPIs COMEX &amp; Activité LLM — dernière mise à jour : {lastRefresh.toLocaleTimeString('fr-FR')}
            {isSimulated && <span className="ml-2 text-orange-500">(simulation)</span>}
          </p>
        </div>
        <button
          onClick={() => { void load(); }}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-muted disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      )}

      {loading && isFirstLoad && (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      )}

      {!isFirstLoad && (
        <>
          {/* Activité du jour (LLM & Résilience) */}
          {dashboard && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground">Activité aujourd&apos;hui (LLM &amp; Résilience)</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Appels LLM</p>
                  <p className="mt-2 text-3xl font-bold text-foreground">{dashboard.total_calls_today.toLocaleString('fr-FR')}</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tokens consommés</p>
                  <p className="mt-2 text-3xl font-bold text-foreground">{dashboard.total_tokens_today.toLocaleString('fr-FR')}</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Coût estimé</p>
                  <p className="mt-2 text-3xl font-bold text-foreground">{dashboard.total_cost_eur.toFixed(2)} €</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Circuit breaker</p>
                  <p className="mt-2 text-3xl font-bold text-foreground">{sdk.circuitBreaker.getState().toUpperCase()}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {sdk.circuitBreaker.getState() === 'Closed' ? '✓ Nominal' : '⚠ Dégradé'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* KPIs Globaux */}
          {kpi && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground">Performance Globale (Objectifs COMEX)</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                  label="Coût moyen / dossier"
                  value={`${kpi.avg_cost_eur.toFixed(3)} €`}
                  target={`< ${TARGET_COST_EUR.toFixed(2)} €`}
                  ok={kpi.avg_cost_eur < TARGET_COST_EUR}
                />
                <KpiCard
                  label="Durée moyenne"
                  value={`${durationMin} min`}
                  target="< 2 min"
                  ok={kpi.avg_duration_ms < TARGET_DURATION_MS}
                />
                <KpiCard
                  label="Taux de succès"
                  value={`${kpi.success_rate_pct.toFixed(1)} %`}
                  target={`> ${TARGET_SUCCESS_PCT} %`}
                  ok={kpi.success_rate_pct >= TARGET_SUCCESS_PCT}
                />
                <KpiCard
                  label="Confiance moyenne"
                  value={`${kpi.avg_confidence_pct.toFixed(1)} %`}
                  target={`> ${TARGET_CONFIDENCE_PCT} %`}
                  ok={kpi.avg_confidence_pct >= TARGET_CONFIDENCE_PCT}
                />
              </div>
            </div>
          )}

          {/* LineChart coût journalier */}
          {kpi && kpi.daily_costs.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-foreground mb-4">
                Coût moyen par dossier — évolution journalière
              </h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={kpi.daily_costs} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(v: number) => `${v.toFixed(2)}€`}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    formatter={(v: number) => [`${v.toFixed(3)} €`, 'Coût moyen']}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                    }}
                  />
                  <ReferenceLine
                    y={TARGET_COST_EUR}
                    stroke="hsl(var(--destructive))"
                    strokeDasharray="4 4"
                    label={{ value: `Cible ${TARGET_COST_EUR}€`, position: 'insideTopRight', fontSize: 10, fill: 'hsl(var(--destructive))' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cost_eur"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 4, fill: 'hsl(var(--primary))' }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Répartition par modèle */}
          {dashboard && (
            <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-foreground mb-3">Répartition par modèle</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="pb-2 font-medium">Modèle</th>
                    <th className="pb-2 font-medium text-right">Appels</th>
                    <th className="pb-2 font-medium text-right">Tokens</th>
                    <th className="pb-2 font-medium text-right">Coût (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(dashboard.models_breakdown).map(([model, stats]) => (
                    <tr key={model} className="border-b border-border/50 last:border-0">
                      <td className="py-2 font-mono text-xs">{model}</td>
                      <td className="py-2 text-right">{stats.calls.toLocaleString('fr-FR')}</td>
                      <td className="py-2 text-right">{stats.tokens.toLocaleString('fr-FR')}</td>
                      <td className="py-2 text-right">{stats.cost_eur.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Résumé volume */}
          {kpi && (
            <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
              <p className="text-sm text-muted-foreground">
                Total traité :{' '}
                <span className="font-semibold text-foreground">
                  {kpi.total_dossiers.toLocaleString('fr-FR')} dossier{kpi.total_dossiers !== 1 ? 's' : ''}
                </span>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

